import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';

import PageLoader from '../../../components/common/PageLoader';
import { STATUS } from '../../../constants';
import { PAYMENT_STATUS } from '../../../constants/preApptConstants';
import { ROUTE_PREFIX_PRE_APPT } from '../../../constants/routes';
import {
  fetchVerifiedWorkflowById,
  isWorkflowVerified,
} from '../../../slices/workflow';
import { getPatientAccessToken } from '../../../utils/patientAccessStorage';
import PaymentLinkStatusView from './PaymentLinkStatusView';
import { PAYMENT_LINK_STATE } from './paymentLinkStates';

const resolvePaymentLinkState = (workflow) => {
  const { appointment, payment } = workflow || {};

  if (payment?.currentStatus?.status === PAYMENT_STATUS.PAID) {
    return PAYMENT_LINK_STATE.PAID;
  }

  if (appointment?.isCancelled) {
    return PAYMENT_LINK_STATE.CANCELLED;
  }

  if (!appointment?.startDateTime) {
    return PAYMENT_LINK_STATE.NOT_RECOGNISED;
  }

  return PAYMENT_LINK_STATE.FEE_PENDING;
};

const NoApptOrPaymentInfoPage = () => {
  const dispatch = useDispatch();
  const { workflowId } = useParams();
  const workflowState = useSelector(({ workflow }) => workflow);
  const { data: workflow, status: workflowFetchingStatus } = workflowState;
  const isVerified = isWorkflowVerified(workflowState, workflowId);
  const [refreshedWorkflowId, setRefreshedWorkflowId] = useState(null);
  const hasAccessToken = Boolean(getPatientAccessToken(workflowId));
  const hasRefreshedWorkflow = refreshedWorkflowId === workflowId;

  useEffect(() => {
    if (!hasAccessToken) return undefined;

    let isMounted = true;

    setRefreshedWorkflowId(null);
    dispatch(fetchVerifiedWorkflowById(workflowId)).finally(() => {
      if (isMounted) setRefreshedWorkflowId(workflowId);
    });

    return () => {
      isMounted = false;
    };
  }, [dispatch, workflowId, hasAccessToken]);

  if (!hasAccessToken) {
    return <Redirect to={`${ROUTE_PREFIX_PRE_APPT}/${workflowId}`} />;
  }

  if (!hasRefreshedWorkflow) {
    return <PageLoader />;
  }

  const hasWorkflowLoaded =
    isVerified &&
    workflowFetchingStatus === STATUS.SUCCESS &&
    !isEmpty(workflow);
  const linkState = hasWorkflowLoaded
    ? resolvePaymentLinkState(workflow)
    : PAYMENT_LINK_STATE.NOT_RECOGNISED;

  return (
    <PaymentLinkStatusView
      linkState={linkState}
      workflow={hasWorkflowLoaded ? workflow : undefined}
    />
  );
};

export default NoApptOrPaymentInfoPage;
